import { Link } from "@tanstack/react-router";
import type { SafetyVideoStatus, Video } from "@unbox/api";
import {
  Calendar,
  Eye,
  FilePlus02,
  ImageIndentLeft,
  LineChartUp02,
  ShieldZap,
  ShoppingBag01,
  VideoRecorder,
  XClose,
} from "@untitledui/icons";
import { useState } from "react";
import { useAssuranceVideo } from "@/api/assurance/video";
import { useVideo } from "@/api/video";
import { useVideoCampaigns } from "@/api/video-campaigns";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Tabs } from "@/components/application/tabs/tabs";
import { ActionRecordDialog } from "@/components/assurance/action-record-dialog";
import { AddToCampaignMenuItem } from "@/components/assurance/add-to-campaign-menu-item";
import { AdCampaignTag, QuarantineTag } from "@/components/assurance/badges/ad-campaign-badge";
import { DetailedVideoStatusBadge } from "@/components/assurance/badges/video-status-badge";
import { InviteToCollaborationModal } from "@/components/assurance/invite-to-collaboration-modal";
import { VideoActionsMenuItems } from "@/components/assurance/video-action-menus";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { BlockCreatorModal } from "@/components/blocklist/block-creator-modal";
import { Transcript } from "@/components/icons/transcript";
import { MessageCreatorsDialog } from "@/components/message-creator-dialogs";
import { Dialog } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useCurrency } from "@/hooks/use-currency";
import { BulkCohortModal } from "@/pages/creator-cohorts/bulk-cohort-modal";
import { AddtoListsModal } from "@/pages/creator-lists/add-to-lists-modal";
import { VideoEmbed } from "@/pages/videos/video-embed";
import { useMerchant } from "@/providers/MerchantProvider";
import { productExplorerRoute } from "@/router";
import { formatCreatorId, formatHashtags } from "@/utils/formatters";
import { getDetectedLanguageLabel } from "@/utils/language";
import { CollapsibleText, VideoInsights } from "./analysis";
import { CampaignsSection } from "./campaigns-section";
import { CollaborationExpiredBadge } from "./collaboration-expired-badge";
import { MetricsGrid, PerformanceChart } from "./performance";
import { ActionsSection, HistorySection, SafetyRisksSection } from "./safety";
import { VideoSafetyStatus } from "./video-safety-status";

const numFormatter = new Intl.NumberFormat("en-US", {
  compactDisplay: "short",
  notation: "compact",
});

const dateFormatter = new Intl.DateTimeFormat("en-CA", {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

// Video Metadata Component
const VideoMetadata = ({
  data,
  isAssurance,
  status,
  hashtags,
  isQuarantined,
  videoId,
  reviewedBy,
  existingRiskIds = [],
}: {
  data: Video;
  isAssurance?: boolean;
  status?: SafetyVideoStatus;
  hashtags?: string[] | string;
  isQuarantined?: boolean;
  videoId?: string;
  reviewedBy?: { name: string; at: string | Date };
  existingRiskIds?: string[];
}) => {
  return (
    <div className="flex flex-col h-auto gap-6 ">
      {isAssurance && status && (
        <div className="flex flex-col gap-1">
          <div className="justify-start text-quaternary text-xs font-normal leading-[18px]">Video safety status</div>
          <div className="flex items-center gap-2">
            {videoId ? (
              <VideoSafetyStatus
                videoId={videoId}
                status={status}
                reviewedBy={reviewedBy}
                existingRiskIds={existingRiskIds}
              />
            ) : (
              <DetailedVideoStatusBadge status={status} showStatusIcon />
            )}
            {videoId && <CollaborationExpiredBadge videoId={videoId} />}
          </div>
        </div>
      )}
      {isAssurance && !status && videoId && (
        <div className="flex flex-col gap-1">
          <CollaborationExpiredBadge videoId={videoId} />
        </div>
      )}
      <div className="flex flex-col gap-2">
        {isQuarantined ? <QuarantineTag /> : data.isInAdCampaign && <AdCampaignTag />}
        <div className="text-xs flex flex-row items-center gap-1">
          <Calendar className="w-4 h-4" strokeWidth={2} />
          Published: {dateFormatter.format(new Date(data.publishDate))}
        </div>
        <div className="text-xs flex flex-row items-center gap-1">
          <Eye className="w-4 h-4" strokeWidth={2} />
          {numFormatter.format(data.views)} views
        </div>
        <div className="text-xs flex flex-row items-center gap-1">
          <VideoRecorder className="w-4 h-4" strokeWidth={2} />
          Video ID:{" "}
          <a
            href={`https://www.tiktok.com/@${(data.creatorId ?? "").replace("CID-", "")}/video/${data.videoId.replace("VID-", "")}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-secondary underline decoration-1 hover:text-brand-secondary_hover transition-colors"
          >
            {data.videoId.replace("VID-", "")}
          </a>
        </div>
      </div>
      {data.creatorId ? (
        <a
          href={`/creator-explorer?creator_id=${data.creatorId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline decoration-dotted decoration-1 underline-offset-2 hover:text-brand-primary transition-colors cursor-pointer"
        >
          {formatCreatorId(data.creatorId)}
        </a>
      ) : (
        <span className="text-tertiary">Unknown creator</span>
      )}

      {isAssurance && hashtags && (
        <div className="flex flex-col gap-1 mt-auto pb-6">
          <div className="justify-start text-quaternary text-xs font-normal leading-[18px]">Hashtags</div>
          <div className="self-stretch justify-start text-primary text-sm font-normal leading-tight">
            {Array.isArray(hashtags) ? formatHashtags(hashtags) : hashtags}
          </div>
        </div>
      )}
    </div>
  );
};

// Products Linked to Video Section Component
type ProductDetailWithMetrics = {
  productId: string;
  productName: string;
  unitsSold: number;
  totalGmv: number;
  lastSaleDate: string | null;
};

const formatRelativeTime = (dateString: string | null): string => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "1 day ago";
  if (diffDays < 30) return `${diffDays} days ago`;
  if (diffDays < 60) return "1 month ago";
  return `${Math.floor(diffDays / 30)} months ago`;
};

const ProductsLinkedSection = ({
  productDetails,
  merchantId,
  currencyFormatter,
}: {
  productDetails: Array<ProductDetailWithMetrics>;
  merchantId: string;
  currencyFormatter: Intl.NumberFormat;
}) => {
  // Only show products that have a resolved productId
  const resolvedProducts = productDetails?.filter((p) => p.productId) ?? [];
  const hasProducts = resolvedProducts.length > 0;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-row items-center gap-2">
        <ShoppingBag01 className="w-5 h-5 text-quaternary" strokeWidth={2} />
        <span className="text-sm font-medium">Products linked to video</span>
      </div>
      <div className="border border-border-primary rounded-lg overflow-hidden">
        {hasProducts ? (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-border-primary">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-tertiary">Product Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-tertiary">Units sold</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-tertiary">Total GMV</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-tertiary">Last Sale</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-primary">
              {resolvedProducts.map((product) => (
                <tr key={product.productId}>
                  <td className="px-4 py-3">
                    <div className="flex flex-col">
                      <Link
                        to={productExplorerRoute.to}
                        params={{ merchantId }}
                        search={{ product_id: product.productId }}
                        className="text-sm text-primary underline decoration-dotted decoration-1 underline-offset-2 hover:text-brand-primary transition-colors"
                      >
                        {product.productName}
                      </Link>
                      <span className="text-xs text-tertiary">ID: {product.productId.replace("PID-", "")}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-primary">{product.unitsSold}</td>
                  <td className="px-4 py-3 text-sm text-success-primary font-medium">
                    {currencyFormatter.format(product.totalGmv)}
                  </td>
                  <td className="px-4 py-3 text-sm text-tertiary">{formatRelativeTime(product.lastSaleDate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="flex items-center justify-center py-5 bg-gray-50">
            <span className="text-sm text-tertiary">No Products linked to Video</span>
          </div>
        )}
      </div>
    </div>
  );
};

type Tab = "performance" | "contentAnalysis" | "safetyRisks";

// Main Inner Component
const Inner = ({
  id,
  defaultTab = "performance",
  isAssurance,
  onClose,
}: {
  id?: string;
  isAssurance?: boolean;
  defaultTab?: Tab;
  onClose: () => void;
}) => {
  const { data } = useVideo(id);
  const [tab, setTab] = useState<Tab>(defaultTab);
  const [openDialog, setOpenDialog] = useState<"add-action" | "send-message">();
  const [preselectedAction, setPreselectedAction] = useState<
    import("@/lib/assurance/action-types").ActionType | undefined
  >();
  const [preselectedCategory, setPreselectedCategory] = useState<"take" | "log" | undefined>();
  const [messageOpen, setMessageOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [addToListOpen, setAddToListOpen] = useState(false);
  const [addToCohortOpen, setAddToCohortOpen] = useState(false);
  const { data: assuranceData } = useAssuranceVideo(isAssurance && id ? id : "");
  const merchantId = useMerchant();
  const { data: videoCampaigns } = useVideoCampaigns(id);

  const currency = useCurrency();
  const currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    notation: "compact",
    compactDisplay: "short",
  });

  if (!data)
    return (
      <div className="flex w-full h-full items-center justify-center">
        <LoadingIndicator />
      </div>
    );

  const hasInsights = !!(data.archetypes && data.tactics && data.hooks && data.sellingPoints);

  const tabs = [
    { id: "performance" as const, label: "Performance", icon: <LineChartUp02 className="w-4 h-4" /> },
    { id: "contentAnalysis" as const, label: "Content Analysis", icon: <ImageIndentLeft className="w-4 h-4" /> },
    ...(isAssurance
      ? [{ id: "safetyRisks" as const, label: "Safety Risks", icon: <ShieldZap className="w-4 h-4" /> }]
      : []),
  ];

  return (
    <>
      {/* Action Buttons - Top Right */}
      <div className="flex flex-row w-full justify-end items-start pt-10 pr-6">
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Dropdown.DotsButton className="flex items-center justify-center p-2 rounded-lg border border-transparent hover:border-border-primary active:border-border-primary data-[state=open]:border-border-primary" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[280px]">
              <VideoActionsMenuItems
                handlers={{
                  onMessageCreator: () => setMessageOpen(true),
                  onAddToList: () => setAddToListOpen(true),
                  onInviteToCollaboration: () => setInviteOpen(true),
                  onAddToCohort: () => setAddToCohortOpen(true),
                  onBlock: () => setBlockOpen(true),
                  onRemoveFromCollaboration: () => {
                    setPreselectedAction("remove_from_collaboration");
                    setPreselectedCategory("take");
                    setOpenDialog("add-action");
                  },
                  cohortsEnabled: true,
                  blocklistEnabled: !!data.tiktokCreatorOpenId,
                }}
              />
              {videoCampaigns?.campaigns.length === 0 && !assuranceData?.quarantine?.isQuarantined && (
                <AddToCampaignMenuItem withSeparator />
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <button
            type="button"
            onClick={onClose}
            className="flex items-center justify-center p-2 rounded-lg cursor-pointer text-fg-quaternary hover:text-fg-quaternary_hover border border-transparent hover:border-border-primary active:border-border-primary transition duration-100 ease-linear"
            aria-label="Close"
          >
            <XClose className="size-5" />
          </button>
        </div>
      </div>
      <div className="p-8 pb-16 pt-0 w-full flex flex-col gap-6">
        <Dialog
          open={openDialog === "add-action"}
          onOpenChange={(open) => {
            setOpenDialog(open ? "add-action" : undefined);
            if (!open) {
              setPreselectedAction(undefined);
              setPreselectedCategory(undefined);
            }
          }}
        >
          {openDialog === "add-action" && (
            <ActionRecordDialog
              videos={[
                {
                  videoId: data.videoId,
                  creatorId: data.creatorId,
                  thumbnail: data.urlFirstFrame,
                  productList: data.productList,
                },
              ]}
              onClose={() => {
                setOpenDialog(undefined);
                setPreselectedAction(undefined);
                setPreselectedCategory(undefined);
              }}
              preselectedAction={preselectedAction}
              preselectedCategory={preselectedCategory}
            />
          )}
        </Dialog>

        {messageOpen && (
          <MessageCreatorsDialog
            recipients={[
              {
                creatorId: data.creatorId ?? "",
                handle: data.creatorId ? data.creatorId.replace("CID-", "") : null,
                thumbnail: data.urlFirstFrame ?? null,
              },
            ]}
            open={messageOpen}
            onOpenChange={setMessageOpen}
            onSuccess={() => setMessageOpen(false)}
          />
        )}

        {inviteOpen && (
          <InviteToCollaborationModal
            creator={{
              openId: data.creatorId ?? "",
              username: data.creatorId ? data.creatorId.replace("CID-", "") : "",
              nickname: data.creatorId ? data.creatorId.replace("CID-", "") : null,
              avatar: data.urlFirstFrame ?? null,
            }}
            open={inviteOpen}
            onOpenChange={setInviteOpen}
          />
        )}

        {blockOpen && data.tiktokCreatorOpenId && (
          <BlockCreatorModal
            creatorOpenIds={[data.tiktokCreatorOpenId]}
            creatorName={data.creatorId ? data.creatorId.replace("CID-", "") : undefined}
            open={blockOpen}
            onOpenChange={setBlockOpen}
          />
        )}

        <AddtoListsModal
          selectedCreators={data.creatorId ? [data.creatorId] : []}
          onAddToList={() => setAddToListOpen(false)}
          open={addToListOpen}
          onOpenChange={setAddToListOpen}
        />

        <BulkCohortModal
          creatorIds={data.creatorId ? [data.creatorId] : []}
          open={addToCohortOpen}
          onOpenChange={setAddToCohortOpen}
          onApplied={() => setAddToCohortOpen(false)}
        />

        {/* Video and Metadata - Title moved to right of video */}
        <div className="flex flex-row w-full items-start gap-6">
          <div className="flex-shrink-0">
            <VideoEmbed url={data.videoUrl} videoId={data.videoId} thumbnail={data.urlFirstFrame} />
          </div>
          <div className="flex flex-col h-auto gap-6 flex-1 min-w-0">
            {/* Title with tooltip */}
            <Tooltip title={data.videoTitle}>
              <TooltipTrigger className="text-left block w-full">
                <h2 className="text-lg font-medium text-primary line-clamp-3 break-words">{data.videoTitle}</h2>
              </TooltipTrigger>
            </Tooltip>

            <VideoMetadata
              data={data}
              isAssurance={isAssurance}
              status={assuranceData?.status}
              hashtags={assuranceData?.hashtags}
              isQuarantined={assuranceData?.quarantine?.isQuarantined}
              videoId={id}
              reviewedBy={
                assuranceData?.manuallyReviewedBy && assuranceData.manuallyReviewedAt
                  ? { name: assuranceData.manuallyReviewedBy.fullName, at: assuranceData.manuallyReviewedAt }
                  : undefined
              }
              existingRiskIds={(assuranceData?.flags ?? [])
                .filter((flag) => !flag.edits.some((edit) => edit.actionTaken === "flag_dismissed"))
                .flatMap((flag) => (flag.riskId ? [flag.riskId] : []))}
            />
          </div>
        </div>

        <Tabs selectedKey={tab} onSelectionChange={(key) => setTab(key as Tab)} className="w-full max-md:hidden ">
          <Tabs.List type="underline" items={tabs}>
            {(tab) => <Tabs.Item {...tab} className="w-full" />}
          </Tabs.List>
        </Tabs>

        {tab === "performance" && (
          <div className="flex flex-col gap-16 pt-8">
            <MetricsGrid data={data} currencyFormatter={currencyFormatter} />
            <ProductsLinkedSection
              productDetails={data.productDetails}
              merchantId={merchantId}
              currencyFormatter={currencyFormatter}
            />
            {id && <PerformanceChart videoId={id} currencyFormatter={currencyFormatter} />}
            {id && <CampaignsSection videoId={id} isQuarantined={assuranceData?.quarantine?.isQuarantined} />}
          </div>
        )}

        {tab === "contentAnalysis" && (
          <>
            {/* Summary */}
            <CollapsibleText
              title="Summary"
              icon={FilePlus02}
              content={data.videoSummary}
              emptyText="No summary generated"
              contentClassName="text-primary font-medium"
            />

            {hasInsights && <VideoInsights data={data} />}

            {data?.transcript && (
              <CollapsibleText
                title={
                  data.detectedLanguage
                    ? `Video Transcript (${getDetectedLanguageLabel(data.detectedLanguage)})`
                    : "Video Transcript (Language not detected)"
                }
                icon={Transcript}
                content={
                  data.englishTranscript && data.englishTranscript !== data.transcript ? (
                    <>
                      {data.englishTranscript}
                      <br />
                      <br />
                      <em>{data.transcript}</em>
                    </>
                  ) : (
                    data.transcript
                  )
                }
                emptyText="No transcript available"
                contentClassName="text-quaternary font-normal"
              />
            )}
          </>
        )}

        {/* Assurance Sections */}
        {tab === "safetyRisks" && assuranceData && (
          <>
            <SafetyRisksSection video={assuranceData} />

            {(assuranceData.status === "riskNeedsAction" ||
              assuranceData.status === "actionInProgress" ||
              assuranceData.status === "riskActioned") && <ActionsSection video={assuranceData} />}
            <HistorySection video={assuranceData} />
          </>
        )}
      </div>
    </>
  );
};

// Main Export Component
export const VideoDetails = ({
  id,
  onClose,
  isAssurance = false,
  defaultTab,
}: {
  id?: string;
  onClose: () => void;
  isAssurance?: boolean;
  defaultTab?: Tab;
}) => {
  if (!id) return null;

  return (
    <Sheet open={!!id} onOpenChange={onClose}>
      <SheetContent className="w-[800px] overflow-y-auto sm:max-w-[800px]" hideCloseButton>
        <Inner id={id} isAssurance={isAssurance} defaultTab={defaultTab} onClose={onClose} />
      </SheetContent>
    </Sheet>
  );
};
